import { FC } from "react";
import { Colors } from "../../../configs";
import { secondaryAlpha } from "../../../configs/colors";

export type PrintCopyChoice = "both" | "storeCopy" | "receipt" | "none";

export type PrintFeedback = { copy: PrintCopyChoice; success: boolean; message?: string };

export const PRINT_OPTIONS: { key: PrintCopyChoice; label: string }[] = [
    { key: "both",      label: "Print Both"    },
    { key: "storeCopy", label: "Store Copy"    },
    { key: "receipt",   label: "Customer Copy" },
];

function PrinterIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[1rem] w-[1rem]" fill="none" aria-hidden="true">
            <path d="M7 8V4h10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 17H5.5A2.5 2.5 0 0 1 3 14.5v-3A2.5 2.5 0 0 1 5.5 9h13a2.5 2.5 0 0 1 2.5 2.5v3a2.5 2.5 0 0 1-2.5 2.5H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 14h10v6H7v-6Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        </svg>
    );
}

function SpinnerIcon() {
    return (
        <svg viewBox="0 0 24 24" className="h-[1rem] w-[1rem] animate-spin" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
            <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
    );
}

interface IPrintCopyButtonsProps {
    onPrintSelect: (copy: PrintCopyChoice) => void;
    printingCopy?: PrintCopyChoice | null;
    printFeedback?: PrintFeedback | null;
}

// "Print Receipt" heading, the Both / Store / Customer buttons and the
// last print's result - shared by every end-of-transaction popup so the
// cashier always gets the same print choices.
const PrintCopyButtons: FC<IPrintCopyButtonsProps> = ({ onPrintSelect, printingCopy, printFeedback }) => (
    <>
        <div
            className="mb-[0.75rem] text-center text-[0.8125rem] font-semibold"
            style={{ color: "#515151" }}
        >
            Print Receipt
        </div>
        <div className="mb-[0.5rem] grid grid-cols-3 gap-[0.5rem]">
            {PRINT_OPTIONS.map((opt) => {
                const isThisPrinting = printingCopy === opt.key;
                const disabled = !!printingCopy;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        disabled={disabled}
                        onClick={() => onPrintSelect(opt.key)}
                        className="flex h-[3.25rem] items-center justify-center gap-[0.625rem] rounded-[0.75rem] border px-[0.75rem] text-[0.75rem] font-bold transition-all duration-150 disabled:cursor-not-allowed enabled:hover:-translate-y-[0.0625rem]"
                        style={{
                            borderColor: Colors.SECONDARY,
                            color: "white",
                            background: `linear-gradient(180deg, ${Colors.SECONDARY} 0%, #0568A7 100%)`,
                            boxShadow: `0 0.5rem 1.125rem ${secondaryAlpha(0.22)}`,
                            opacity: disabled && !isThisPrinting ? 0.5 : 1,
                        }}
                    >
                        <span
                            className="flex h-[1.75rem] w-[1.75rem] shrink-0 items-center justify-center rounded-full"
                            style={{ backgroundColor: "rgba(255,255,255,0.16)", color: "white" }}
                        >
                            {isThisPrinting ? <SpinnerIcon /> : <PrinterIcon />}
                        </span>
                        <span className="whitespace-nowrap tracking-[-0.006em]">
                            {isThisPrinting ? "Printing…" : opt.label}
                        </span>
                    </button>
                );
            })}
        </div>

        {printFeedback && (
            <div
                className="mb-[1rem] rounded-[0.5rem] px-[0.75rem] py-[0.5rem] text-center font-semibold"
                style={{
                    fontSize: "var(--fs-2xs)",
                    backgroundColor: printFeedback.success ? `${Colors.PRIMARY}14` : "rgba(147,0,0,0.08)",
                    color: printFeedback.success ? "#1B6B2A" : "#930000",
                }}
            >
                {printFeedback.success
                    ? `${PRINT_OPTIONS.find((o) => o.key === printFeedback.copy)?.label ?? "Receipt"} sent to the printer.`
                    : (printFeedback.message ?? "Print failed.")}
            </div>
        )}
    </>
);

export default PrintCopyButtons;
